import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { KanbanSquare, CheckCircle2, Clock, AlertTriangle, MessageSquare, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY, CATEGORY, initials, fmtDate, isOverdue, Dot } from "@/lib/ui";
import { MyRequestsCard, PendingReimbursementsCard, OnLeaveTodayCard } from "@/components/requests/DashboardCards";
import { AvatarStack, assigneeIds } from "@/components/Assignees";
import { MySubtaskList, useMySubtasks } from "@/components/MyWork";
import { UpcomingHolidaysCard, AnnouncementsCard } from "@/components/hr/HrBits";

function MiniStat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-xl font-display font-bold text-slate-900 leading-none">{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>
    </div>
  );
}

function TaskRow({ t, onOpen, users }) {
  const overdue = isOverdue(t.due_date, t.category);
  return (
    <button onClick={() => onOpen(t)} data-testid={`eng-task-${t.id}`}
      className="w-full text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all flex items-center gap-4">
      <Dot className={PRIORITY[t.priority].dot} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-slate-200 text-slate-500">{t.board_name}</Badge>
          <Badge variant="outline" className={`h-4 px-1.5 text-[10px] ${CATEGORY[t.category]?.chip}`}>{t.stage_name}</Badge>
          {t.subtasks_total > 0 && <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{t.subtasks_done}/{t.subtasks_total}</span>}
          {t.notes_count > 0 && <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{t.notes_count}</span>}
        </div>
      </div>
      {t.due_date && (
        <span className={`text-xs inline-flex items-center gap-1 shrink-0 ${overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
          <Calendar className="w-3 h-3" />{fmtDate(t.due_date)}
        </span>
      )}
      <div className="shrink-0">
        {assigneeIds(t).length ? <AvatarStack ids={assigneeIds(t)} users={users} size="w-7 h-7" /> : <Avatar className="w-7 h-7"><AvatarFallback className="bg-slate-100 text-slate-500 text-[10px]">?</AvatarFallback></Avatar>}
      </div>
    </button>
  );
}

export default function EngineerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState({});

  useEffect(() => {
    (async () => {
      const [t, u] = await Promise.all([api.get("/tasks"), api.get("/users")]);
      setTasks(t.data);
      setUsers(Object.fromEntries(u.data.map((x) => [x.id, x])));
    })();
  }, []);

  const mine = useMemo(() => tasks.filter((t) => assigneeIds(t).includes(user?.id)), [tasks, user]);
  const mySubs = useMySubtasks();
  const stats = useMemo(() => ({
    total: mine.length,
    done: mine.filter((t) => t.category === "completed").length,
    active: mine.filter((t) => ["backlog", "in_progress", "in_review"].includes(t.category)).length,
    overdue: mine.filter((t) => isOverdue(t.due_date, t.category)).length,
  }), [mine]);

  const open = (t) => navigate(`/boards/${t.board_id}?task=${t.id}`);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="engineer-dashboard">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">My Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Hi {user?.name?.split(" ")[0]} — your assigned work and the full team view.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MiniStat icon={KanbanSquare} label="My tasks" value={stats.total} tint="bg-indigo-50 text-indigo-600" />
        <MiniStat icon={Clock} label="Active" value={stats.active} tint="bg-amber-50 text-amber-600" />
        <MiniStat icon={CheckCircle2} label="Completed" value={stats.done} tint="bg-emerald-50 text-emerald-600" />
        <MiniStat icon={AlertTriangle} label="Overdue" value={stats.overdue} tint="bg-red-50 text-red-600" />
      </div>

      <div className={`grid grid-cols-1 ${user?.is_hr ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-4 mb-6`}>
        <MyRequestsCard />
        <UpcomingHolidaysCard />
        <AnnouncementsCard />
        {user?.is_hr && <PendingReimbursementsCard />}
        {user?.is_hr && <OnLeaveTodayCard />}
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="mb-4">
          <TabsTrigger value="mine" data-testid="engineer-my-tasks-tab">My tasks ({mine.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="engineer-all-tasks-tab">All tasks ({tasks.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="mine" className="space-y-2.5">
          {mine.map((t) => <TaskRow key={t.id} t={t} onOpen={open} users={users} />)}
          {mine.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No tasks assigned to you yet.</p>}
          {mySubs.length > 0 && <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 pt-4">My subtasks ({mySubs.filter((s) => !s.done).length} open)</p>}
          <MySubtaskList subs={mySubs} testid="engineer-my-subtasks" />
        </TabsContent>
        <TabsContent value="all" className="space-y-2.5">
          {tasks.map((t) => <TaskRow key={t.id} t={t} onOpen={open} users={users} />)}
          {tasks.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No tasks yet.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
